/**
 * Centralized error → toast helper.
 *
 * Why: before this, 50+ catch blocks across the app each spelled their own
 * `toast.error(err.message || "Eroare la X")` with slightly different
 * fallback wording, no logging, and no special handling for known Supabase
 * error shapes (PostgrestError, FunctionsHttpError, RLS-denied codes).
 *
 * Usage:
 *   } catch (err) {
 *     handleError(err, "Eroare la salvarea profilului");
 *   }
 *
 * Or inside React Query mutations:
 *   onError: (err) => handleError(err, "Eroare la ștergere"),
 *
 * The helper:
 *   1. Logs the raw error to console (with the fallback as the prefix tag),
 *      so devs see the full stack — toast users see only the friendly RO text.
 *   2. Extracts a human message from Error / PostgrestError / FunctionsHttpError
 *      / plain string / unknown.
 *   3. Translates a few common Supabase / network codes to friendly Romanian
 *      copy so the toast is never just "JWT expired" or "fetch failed".
 *   4. Falls back to the provided RO message if nothing better is available.
 */
import { toast } from "sonner";

type AnyError =
  | Error
  | { message?: string; code?: string; status?: number; details?: string }
  | string
  | null
  | undefined
  | unknown;

/**
 * Map known Supabase / PostgREST / network error signatures to RO copy.
 * Returns null if no match — caller should use the raw message or fallback.
 */
function translateKnownError(err: AnyError): string | null {
  if (!err || typeof err !== "object") return null;
  const e = err as { code?: string; status?: number; message?: string };
  const msg = (e.message || "").toLowerCase();

  // PostgREST RLS-denied (most common surprise during dev/QA).
  if (e.code === "42501" || msg.includes("row-level security")) {
    return "Nu ai permisiunea pentru această acțiune.";
  }
  // PostgREST: row not found via .single()
  if (e.code === "PGRST116") {
    return "Înregistrarea nu a fost găsită.";
  }
  // Auth: expired session
  if (msg.includes("jwt expired") || msg.includes("invalid jwt")) {
    return "Sesiunea a expirat. Reconectează-te.";
  }
  // Network / offline
  if (msg.includes("failed to fetch") || msg.includes("networkerror")) {
    return "Conexiunea la server a eșuat. Verifică internetul și încearcă din nou.";
  }
  // Edge function gateway timeout
  if (e.status === 504 || msg.includes("timeout")) {
    return "Serverul a răspuns prea greu. Încearcă din nou.";
  }
  return null;
}

/**
 * Best-effort extraction of a printable message from any thrown value.
 */
function extractMessage(err: AnyError): string | null {
  if (!err) return null;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const e = err as { message?: string; details?: string };
    return e.message || e.details || null;
  }
  return null;
}

export interface HandleErrorOptions {
  /** Override the toast title. Defaults to the friendly/raw error message. */
  title?: string;
  /** Suppress console.error (rarely useful — mostly for noisy expected errors). */
  silent?: boolean;
}

/**
 * Show a Romanian-friendly error toast and log the raw error.
 *
 * @param err       The thrown value (any shape).
 * @param fallback  RO message shown when no better text can be extracted.
 *                  Also used as the `console.error` tag.
 * @param options   Optional overrides.
 */
export function handleError(
  err: AnyError,
  fallback: string,
  options: HandleErrorOptions = {},
): void {
  if (!options.silent) {
    // eslint-disable-next-line no-console
    console.error(`[${fallback}]`, err);
  }
  const friendly = translateKnownError(err);
  const raw = extractMessage(err);
  const message = options.title ?? friendly ?? raw ?? fallback;
  toast.error(message);
}
